import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, MessageCircle, AlertTriangle, Clock, CreditCard, Upload, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact Support Screen for Deactivated Users
const ContactSupportScreen = ({ reason, supportContact }) => {
  const whatsappNumber = supportContact?.support_whatsapp?.replace(/[^0-9]/g, '') || '916382947171';
  const phoneDisplay = supportContact?.support_phone || '+91 63829 47171';
  
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="text-red-600" size={40} />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Account Suspended</h1>
        <p className="text-slate-600 mb-6">
          {reason || 'Your account has been deactivated. Please contact customer support for assistance.'}
        </p>
        
        <div className="space-y-4">
          <a
            href={`tel:${supportContact?.support_phone || '+916382947171'}`}
            className="flex items-center justify-center gap-3 w-full py-4 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors"
            data-testid="call-support-btn"
          >
            <Phone size={20} />
            Call Support: {phoneDisplay}
          </a>
          
          <a
            href={`https://wa.me/${whatsappNumber}?text=Hi%2C%20I%20need%20help%20with%20my%20GPOS%20account`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full py-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
            data-testid="whatsapp-support-btn"
          >
            <MessageCircle size={20} />
            WhatsApp Support
          </a>
        </div>
        
        <p className="text-xs text-slate-400 mt-6">
          Support hours: Mon-Sat, 9 AM - 6 PM IST
        </p>
      </div>
    </div>
  );
};

// Pending Approval Screen
const PendingApprovalScreen = ({ submittedAt, supportContact }) => {
  const whatsappNumber = supportContact?.support_whatsapp?.replace(/[^0-9]/g, '') || '918248075883';
  
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="text-amber-600" size={40} />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Payment Under Review</h1>
        <p className="text-slate-600 mb-4">
          Your payment has been submitted and is pending approval from our team.
        </p>
        
        {submittedAt && (
          <p className="text-sm text-slate-500 mb-6">
            Submitted: {new Date(submittedAt).toLocaleString('en-IN')}
          </p>
        )}
        
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-amber-800">
            Approval usually takes 1-2 business hours. You'll get access immediately once approved.
          </p>
        </div>
        
        <div className="space-y-3">
          <a
            href={`https://wa.me/${whatsappNumber}?text=Hi%2C%20I%20submitted%20a%20payment%20for%20GPOS%20and%20waiting%20for%20approval`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
          >
            <MessageCircle size={18} />
            Need faster approval? WhatsApp us
          </a>
          
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50"
          >
            Refresh Status
          </button>
        </div>
      </div>
    </div>
  );
};

// Plan Selection and Payment Screen
const PlanSelectionScreen = ({ onPaymentSubmitted }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('select'); // select, pay, form
  const [formData, setFormData] = useState({
    amount: '',
    utr_number: '',
    attachment: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, qrRes] = await Promise.all([
        axios.get(`${API}/public/plans`),
        axios.get(`${API}/public/payment-qr`)
      ]);
      setPlans(plansRes.data);
      setQrCode(qrRes.data.qr_code);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, attachment: reader.result });
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async () => {
    if (!formData.amount || !formData.utr_number) {
      setError('Please fill in amount and UTR number');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/pos/submit-payment`, {
        plan_id: selectedPlan.id,
        amount: parseFloat(formData.amount),
        utr_number: formData.utr_number,
        attachment_base64: formData.attachment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onPaymentSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  // Step 1: Select Plan
  if (step === 'select') {
    return (
      <div className="min-h-screen bg-slate-100 p-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Choose Your Plan</h1>
            <p className="text-slate-600">Select a subscription plan to continue using GPOS</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`bg-white rounded-xl p-6 cursor-pointer transition-all border-2 ${
                  selectedPlan?.id === plan.id
                    ? 'border-violet-600 ring-2 ring-violet-200'
                    : 'border-transparent hover:border-slate-200'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                <h3 className="text-xl font-bold text-slate-800 mb-2">{plan.name}</h3>
                <div className="text-3xl font-bold text-violet-600 mb-4">
                  ₹{plan.price_inr}
                  <span className="text-sm text-slate-500 font-normal">/month</span>
                </div>
                <ul className="space-y-2">
                  {plan.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check size={16} className="text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {selectedPlan?.id === plan.id && (
                  <div className="mt-4 py-2 bg-violet-100 text-violet-700 text-center rounded-lg text-sm font-medium">
                    Selected
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => selectedPlan && setStep('pay')}
              disabled={!selectedPlan}
              className="px-8 py-4 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="continue-to-payment-btn"
            >
              Continue to Payment
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Show QR & Payment Info
  if (step === 'pay') {
    return (
      <div className="min-h-screen bg-slate-100 p-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Complete Payment</h1>
            <p className="text-slate-600">Scan the QR code to pay</p>
          </div>

          <div className="bg-white rounded-xl p-6 mb-6">
            <div className="bg-violet-50 rounded-xl p-4 mb-4 text-center">
              <p className="text-sm text-violet-600">Selected Plan</p>
              <p className="text-xl font-bold text-violet-700">{selectedPlan.name}</p>
              <p className="text-3xl font-bold text-violet-800 mt-1">₹{selectedPlan.price_inr}</p>
            </div>

            {qrCode ? (
              <div className="flex justify-center mb-4">
                <img 
                  src={qrCode} 
                  alt="Payment QR" 
                  className="w-64 h-64 object-contain border-2 border-slate-200 rounded-xl p-2"
                />
              </div>
            ) : (
              <div className="w-64 h-64 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <p className="text-slate-400">QR not available</p>
              </div>
            )}

            <div className="text-center text-sm text-slate-600">
              <p>Scan with any UPI app</p>
              <p className="text-xs text-slate-400 mt-1">GPay, PhonePe, Paytm, etc.</p>
            </div>
          </div>

          <button
            onClick={() => setStep('form')}
            className="w-full py-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
            data-testid="payment-done-btn"
          >
            I've Made the Payment
          </button>

          <button
            onClick={() => setStep('select')}
            className="w-full py-3 mt-3 text-slate-600 hover:text-slate-800"
          >
            ← Back to Plans
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Payment Form
  return (
    <div className="min-h-screen bg-slate-100 p-4 py-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Submit Payment Details</h1>
          <p className="text-slate-600">Enter your transaction details for verification</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-4 flex items-center gap-2">
            <X size={18} />
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Amount Paid (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder={`${selectedPlan?.price_inr || 0}`}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="payment-amount-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              UTR / Transaction Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.utr_number}
              onChange={(e) => setFormData({ ...formData, utr_number: e.target.value })}
              placeholder="e.g., 123456789012"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="payment-utr-input"
            />
            <p className="text-xs text-slate-400 mt-1">12-digit UPI transaction reference</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Payment Screenshot (Optional)
            </label>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="payment-screenshot-input"
              />
              <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
                formData.attachment ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-violet-300'
              }`}>
                {formData.attachment ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <Check size={20} />
                    Screenshot attached
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-slate-500">
                    <Upload size={20} />
                    Tap to upload screenshot
                  </div>
                )}
              </div>
            </label>
          </div>

          <button
            onClick={handleSubmitPayment}
            disabled={submitting || !formData.amount || !formData.utr_number}
            className="w-full py-4 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            data-testid="submit-payment-btn"
          >
            {submitting ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>

        <button
          onClick={() => setStep('pay')}
          className="w-full py-3 mt-3 text-slate-600 hover:text-slate-800"
        >
          ← Back
        </button>
      </div>
    </div>
  );
};

// Expired Subscription Screen
const ExpiredScreen = ({ expiredAt, onRenew, supportContact }) => {
  const whatsappNumber = supportContact?.support_whatsapp?.replace(/[^0-9]/g, '') || '918248075883';
  
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CreditCard className="text-red-600" size={40} />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Subscription Expired</h1>
        <p className="text-slate-600 mb-4">
          Your subscription expired on {new Date(expiredAt).toLocaleDateString('en-IN')}.
          Please renew to continue using GPOS.
        </p>
        
        <button
          onClick={onRenew}
          className="w-full py-4 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors"
          data-testid="renew-subscription-btn"
        >
          Renew Subscription
        </button>
        
        <div className="mt-6 pt-6 border-t border-slate-200">
          <p className="text-sm text-slate-500 mb-3">Need help?</p>
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-green-600 hover:text-green-700"
          >
            <MessageCircle size={18} />
            WhatsApp Support
          </a>
        </div>
      </div>
    </div>
  );
};

// Main Component that decides which screen to show
const SubscriptionGate = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRenewal, setShowRenewal] = useState(false);
  const [supportContact, setSupportContact] = useState(null);

  useEffect(() => {
    checkSubscription();
    fetchSupportSettings();
  }, []);

  const fetchSupportSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      setSupportContact(response.data);
    } catch (err) {
    }
  };

  const checkSubscription = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/pos/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (err) {
      console.error('Error checking subscription:', err);
      // If error, allow access (fail-open for existing users)
      setStatus({ status: 'active', can_access: true });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  // No token means not logged in - let the normal flow handle it
  if (!localStorage.getItem('pos_token')) {
    return children;
  }

  // Deactivated user - show contact support
  if (status?.status === 'deactivated') {
    return <ContactSupportScreen reason={status.reason} supportContact={supportContact} />;
  }

  // Pending approval
  if (status?.status === 'pending_approval') {
    return <PendingApprovalScreen submittedAt={status.submitted_at} supportContact={supportContact} />;
  }

  // No subscription - show plan selection
  if (status?.status === 'no_subscription' || showRenewal) {
    return (
      <PlanSelectionScreen 
        onPaymentSubmitted={() => {
          setShowRenewal(false);
          checkSubscription();
        }} 
      />
    );
  }

  // Expired - show expired screen
  if (status?.status === 'expired') {
    return (
      <ExpiredScreen 
        expiredAt={status.expired_at} 
        onRenew={() => setShowRenewal(true)}
        supportContact={supportContact}
      />
    );
  }

  // Active subscription - show normal content
  return children;
};

export default SubscriptionGate;
